import React, { useState, useRef } from 'react'
import { T, Sheet, fmtOdds } from '../nfl/ui'
import { checkNBASlip } from './api'

// NBA Check my slip — the NFL flow (nfl/slipcheck.jsx) for NBA: drop a screenshot (read on the
// phone by Tesseract, never uploaded) or paste the slip; every leg is graded by the model vs
// the price, plus the whole-ticket chance vs what the payout implies. Research only.
const TESS_SRC = 'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js'
let _tess = null
const loadTesseract = () => _tess || (_tess = new Promise((ok, fail) => {
  if (window.Tesseract) return ok(window.Tesseract)
  const s = document.createElement('script')
  s.src = TESS_SRC; s.async = true
  s.onload = () => (window.Tesseract ? ok(window.Tesseract) : fail(new Error('reader unavailable')))
  s.onerror = () => { _tess = null; fail(new Error('reader unavailable')) }
  document.head.appendChild(s)
}))
const SHORT = { points: 'PTS', rebounds: 'REB', assists: 'AST', threes: '3PM', pra: 'PRA' }
const pctS = p => (p == null ? '—' : `${Math.round(p * 100)}%`)
const oneIn = p => (p > 0 ? `about 1 in ${Math.max(1, Math.round(1 / p))}` : '')
const VERDICT = {
  value: { txt: '✅ Good price', c: T.green }, priced_in: { txt: '👍 Fair price', c: T.muted2 },
  overpriced: { txt: '❌ Short price', c: T.red }, model_only: { txt: 'No price to compare', c: T.muted },
}
const legLabel = l => l.prop_type === 'threes' || l.side === 'under' || (l.line % 1 !== 0.5)
  ? `${l.side === 'under' ? 'Under' : 'Over'} ${l.line} ${SHORT[l.prop_type] || l.prop_type}`
  : `${l.line + 0.5}+ ${SHORT[l.prop_type] || l.prop_type}`

function Leg({ l, weakest }) {
  const v = VERDICT[l.verdict] || {}
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 0', borderTop: `1px solid ${T.border}` }}>
      {l.headshot ? <img src={l.headshot} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', background: T.card }} />
        : <span style={{ width: 34, height: 34, borderRadius: '50%', background: T.card, display: 'inline-block' }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800 }}>{l.player}{weakest && <span style={{ color: T.amber, fontSize: 11 }}> · weakest leg</span>}</div>
        <div style={{ fontSize: 12, color: T.muted2 }}>{l.status === 'ok' ? legLabel(l) : l.raw}
          {l.line_assumed && <span style={{ color: T.amber }}> · line not read, used the board's</span>}</div>
        {l.status === 'ok'
          ? <div style={{ fontSize: 11, color: v.c, marginTop: 2 }}>{v.txt}
              {l.book_odds != null && <span style={{ color: T.muted }}> · {fmtOdds(l.book_odds)}{l.price_src === 'slip' ? ' (your slip)' : ''} = {pctS(l.book_prob)}</span>}
              {l.proj != null && <span style={{ color: T.muted }}> · proj {l.proj}</span>}</div>
          : <div style={{ fontSize: 11, color: T.amber, marginTop: 2 }}>Not on tonight's board</div>}
      </div>
      <b style={{ fontFamily: T.mono, fontSize: 16, color: l.prob >= 0.6 ? T.green : l.prob >= 0.45 ? T.amber : T.red }}>{pctS(l.prob)}</b>
    </div>
  )
}

export function SlipCheck({ season, date, onClose }) {
  const [mode, setMode] = useState('shot')
  const [text, setText] = useState('')
  const [img, setImg] = useState(null)
  const [reading, setReading] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState(null)
  const [err, setErr] = useState('')
  const fileRef = useRef(null)
  const check = async (t) => {
    const body = (t ?? text).trim()
    if (!body) { setErr('Paste your slip first.'); return }
    setBusy(true); setErr(''); setRes(null)
    const r = await checkNBASlip(body, season, date)
    setBusy(false)
    if (r._error) { setErr('Couldn’t check the slip right now. Try again in a minute.'); return }
    if (!r.legs?.length) { setErr('We couldn’t find any legs we recognize. Check the names below, or paste the slip.'); setMode('paste') }
    setRes(r)
  }
  const onFile = async (f) => {
    if (!f) return
    setErr(''); setRes(null); setImg(URL.createObjectURL(f)); setReading('Loading the reader…')
    try {
      const Tess = await loadTesseract()
      setReading('Reading your slip on this phone…')
      const { data } = await Tess.recognize(f, 'eng')
      const t = (data?.text || '').trim()
      setText(t); setReading('')
      if (t) await check(t); else setErr('We couldn’t read that image. Try a clearer screenshot, or paste the text.')
    } catch (_) { setReading(''); setErr('The screenshot reader didn’t load. Paste the slip text instead.'); setMode('paste') }
  }
  const tab = (k, lbl) => <button onClick={() => { setMode(k); setErr('') }} style={{ cursor: 'pointer', border: 'none', borderRadius: 8,
    padding: '9px 0', fontSize: 12.5, fontWeight: 800, color: mode === k ? '#04140D' : T.muted, background: mode === k ? T.green : 'transparent' }}>{lbl}</button>
  const graded = (res?.legs || []).filter(l => l.status === 'ok')
  const share = () => {
    const lines = graded.map(l => `${l.player} ${legLabel(l)} — ${pctS(l.prob)} · ${(VERDICT[l.verdict] || {}).txt || ''}`)
    const txt = `My slip, graded by LINEBRKR NBA\n${lines.join('\n')}${res.ticket_prob != null ? `\nWhole ticket: ${pctS(res.ticket_prob)}` : ''}`
    try { if (navigator.share) navigator.share({ text: txt }); else navigator.clipboard.writeText(txt) } catch (_) {}
  }
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: '6px 16px 26px' }}>
        <div style={{ fontSize: 19, fontWeight: 900 }}>🧾 Check my slip</div>
        <div style={{ fontSize: 12, color: T.muted2, marginTop: 3, lineHeight: 1.45 }}>
          Drop a screenshot or paste your slip. We match every leg to the model and show the real odds.</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 11, padding: 3, marginTop: 12 }}>{tab('shot', '📷 Screenshot')}{tab('paste', '📋 Paste text')}</div>
        {mode === 'shot' ? (
          <div style={{ marginTop: 10 }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onFile(e.target.files?.[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={!!reading || busy} style={{ width: '100%', cursor: 'pointer', display: 'flex',
              alignItems: 'center', gap: 10, textAlign: 'left', color: T.text, background: T.card, border: `1px dashed ${T.green}88`, borderRadius: 11, padding: 10 }}>
              {img ? <img src={img} alt="Your slip" style={{ width: 70, maxHeight: 110, objectFit: 'cover', borderRadius: 6 }} /> : <span style={{ fontSize: 26 }}>📷</span>}
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800 }}>{img ? 'Choose a different screenshot' : 'Choose a slip screenshot'}</span>
                <span style={{ display: 'block', fontSize: 11, color: reading ? T.green : T.muted, marginTop: 3 }}>{reading || 'Read on your phone. The image is never uploaded.'}</span>
              </span>
            </button>
            {text && !reading && <div style={{ fontSize: 10.5, color: T.muted, marginTop: 6 }}>Misread something?{' '}
              <button onClick={() => setMode('paste')} style={{ background: 'none', border: 'none', color: T.green, fontWeight: 800, fontSize: 10.5, cursor: 'pointer', padding: 0 }}>Edit the text</button></div>}
          </div>
        ) : (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 6, lineHeight: 1.4 }}>
              iPhone tip: open your slip screenshot, press and hold the text, tap <b>Copy</b>, then paste here.</div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={7}
              placeholder={'Nikola Jokic Over 12.5 Rebounds\nLeBron James 25+ Points\nStephen Curry 4+ Threes Made'}
              style={{ width: '100%', background: T.card, color: T.text, border: `1px solid ${T.green}88`, borderRadius: 10, padding: 9,
                fontSize: 12, fontFamily: T.mono, resize: 'vertical', boxSizing: 'border-box' }} />
            <button onClick={() => check()} disabled={busy} style={{ width: '100%', marginTop: 8, padding: '12px 0', fontSize: 14.5, fontWeight: 900,
              background: T.green, color: '#04140D', border: 'none', borderRadius: 10, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Checking…' : 'Check my slip'}</button>
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: T.amber, marginTop: 10, lineHeight: 1.4 }}>{err}</div>}
        {res && res.legs?.length > 0 && (
          <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, padding: 10, marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Your slip · {res.legs.length} leg{res.legs.length === 1 ? '' : 's'}{res.parlay_odds != null ? ` · ${fmtOdds(res.parlay_odds)}` : ''}</div>
            {res.legs.map((l, i) => <Leg key={i} l={l} weakest={graded.length > 1 && l.player === res.weakest} />)}
            {res.ticket_prob != null && graded.length > 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: res.payout_prob != null ? '1fr 1fr' : '1fr', gap: 6, marginTop: 10 }}>
                <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, padding: 8, textAlign: 'center' }}>
                  <div style={{ fontFamily: T.mono, fontSize: 19, fontWeight: 900, color: res.payout_prob != null && res.ticket_prob > res.payout_prob ? T.green : T.text }}>{pctS(res.ticket_prob)}</div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>model: whole ticket hits<br />({oneIn(res.ticket_prob)})</div>
                </div>
                {res.payout_prob != null && <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, padding: 8, textAlign: 'center' }}>
                  <div style={{ fontFamily: T.mono, fontSize: 19, fontWeight: 900 }}>{pctS(res.payout_prob)}</div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>what {fmtOdds(res.parlay_odds)} implies<br />({oneIn(res.payout_prob)})</div>
                </div>}
              </div>)}
            {res.same_game && <div style={{ fontSize: 11, color: T.muted2, marginTop: 8 }}>Legs from the same game tend to move together, so the true ticket odds can differ.</div>}
            {graded.length > 0 && <button onClick={share} style={{ width: '100%', marginTop: 10, padding: '11px 0', borderRadius: 10, cursor: 'pointer',
              fontWeight: 900, fontSize: 13.5, color: '#04140D', background: T.green, border: 'none' }}>⤴ Share my slip read</button>}
            {res.unmatched?.length > 0 && <div style={{ fontSize: 11, color: T.amber, marginTop: 8, lineHeight: 1.4 }}>
              Not graded: {res.unmatched.join(' · ')}. That player isn’t on tonight’s board (often out), or the name was misread.</div>}
          </div>)}
        <div style={{ fontSize: 9.5, color: T.faint, marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
          Research, not advice · 21+ · 1-800-GAMBLER · screenshots are read on your phone and never uploaded</div>
      </div>
    </Sheet>
  )
}

export function SlipCheckButton({ season, date }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left',
        color: T.text, background: T.card, border: `1px solid ${T.border2}`, borderRadius: 14, padding: '13px 15px', marginTop: 12 }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>🧾</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 16, fontWeight: 900 }}>Check my slip</span>
          <span style={{ display: 'block', fontSize: 12, color: T.muted2, marginTop: 2, lineHeight: 1.4 }}>
            Drop a screenshot or paste a slip and see each leg graded by the model.</span>
        </span>
        <span style={{ color: T.green, fontWeight: 900, fontSize: 16 }}>›</span>
      </button>
      {open && <SlipCheck season={season} date={date} onClose={() => setOpen(false)} />}
    </>
  )
}
